import { 
  Box, 
  Button, 
  Container, 
  Flex, 
  Heading, 
  Text, 
  VStack, 
  HStack, 
  Image, 
  Icon,
  IconButton,
  Spinner,
  AspectRatio,
  SimpleGrid,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
} from '@chakra-ui/react';
// Check for WebP support
const isWebpSupported = (() => {
  try {
    const canvas = document.createElement('canvas');
    if (!!(canvas.getContext && canvas.getContext('2d'))) {
      return canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0;
    }
    return false;
  } catch (e) {
    return false;
  }
})();


import { keyframes } from "@emotion/react";
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { FaThumbtack, FaPlus, FaCheck, FaExpand, FaCompress, FaGamepad } from 'react-icons/fa';
import React, { useEffect, useState, useCallback } from 'react';
import AppHeader from '../components/AppHeader';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../contexts/AuthContext';
import AppButton, { AppItemProps } from '../components/AppButton';
import AddPinButton from '../components/AddPinButton';
import { handleImageError, extractEmbedId } from '../utils/imageHelpers';

// Subtle glow animation
const pulseGlow = keyframes`
  0% { box-shadow: 0 0 5px 0 rgba(255, 255, 255, 0.3); }
  50% { box-shadow: 0 0 20px 0 rgba(255, 255, 255, 0.4); }
  100% { box-shadow: 0 0 5px 0 rgba(255, 255, 255, 0.3); }
`;

// Category row component
const CategoryRow = ({
  title,
  games,
  color = "blue",
  onPinToggle,
  isPinMode = false
}: {
  title: string;
  games: Game[];
  color?: string;
  onPinToggle?: (game: Game) => void;
  isPinMode?: boolean;
}) => {
  const navigate = useNavigate();
  
  return (
    <Box mb={8}>
      <Heading 
        size="md" 
        color="white" 
        fontWeight="semibold" 
        letterSpacing="wider"
        mb={4}
        pl={4}
        textAlign="left"
      >
        {title}
      </Heading>
      <Box 
        position="relative"
        overflowX="auto"
        p={1}
        css={{
          '&::-webkit-scrollbar': {
            display: 'none'
          },
          msOverflowStyle: 'none',
          scrollbarWidth: 'none'
        }}
      >
        <HStack spacing={4} px={4} py={2}>
          {games.map(game => (
            <Box key={game.id} flex="0 0 auto">
              <AppButton 
                item={{
                  id: game.id,
                  name: game.name,
                  url: game.url || `/games/${game.id}`,
                  color: game.color,
                  isPinned: game.isPinned,
                  isPinMode: isPinMode,
                  description: game.description
                }}
                type="game"
                imageUrl={game.thumbnail}
                onClick={() => navigate(game.url || `/games/${game.id}`)}
                onPinToggle={onPinToggle ? () => onPinToggle(game) : undefined}
              />
            </Box>
          ))}
        </HStack>
      </Box>
    </Box>
  );
};

// Interface for game objects
interface Game {
  id: string;
  name: string;
  thumbnail: string;
  description: string;
  color: string;
  url?: string;
  isPinned?: boolean;
}

// Interface for game category
interface GameCategory {
  title: string;
  color: string;
  games: Game[];
}

// Main component
const GamesPage = () => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const { user, userProfile, isSubscriber } = useAuth();
  const navigate = useNavigate();
  const isAuthenticated = !!user;

  // Pin mode state
  const [isPinMode, setIsPinMode] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  // Pinned games state
  const [pinnedGames, setPinnedGames] = useState<Game[]>([]);
  const [isLoadingPinnedGames, setIsLoadingPinnedGames] = useState(true);

  // Define text color and background gradient
  const textColor = "white";
  const bgGradient = "linear(to-b, #1a1a2e, #16213e, #0f3460)";

  // Extract the embed ID from the thumbnail URL
  const extractEmbedId = (thumbnailUrl: string): string => {
    // Try to extract the ID from the URL pattern
    const matches = thumbnailUrl.match(/(\d+)_[a-f0-9]+\.(?:png|jpg)/);
    if (matches && matches[1]) {
      return matches[1];
    }
    
    // For previews like "mvscu.png" that don't follow the pattern
    const previewMatches = thumbnailUrl.match(/\/([^\/]+)\.png$/);
    if (previewMatches && previewMatches[1]) {
      // Just return the filename without extension as a fallback
      return previewMatches[1];
    }
    
    // Default fallback
    return "game";
  };

  // Define the games categories with real game images and data
  const gameCategories: GameCategory[] = [
    {
      title: "Sega Genesis Games",
      color: "blue",
      games: [
        {
          id: "sonic-classic-heroes",
          name: "Sonic Classic Heroes",
          thumbnail: "/images/games/42046_d54bf187465730456726325ebbe53759b4772496.webp",
          description: "A popular Sonic hack that allows playing with all three characters: Sonic, Tails, and Knuckles together.",
          color: "blue",
          url: "/games/42046-sonic-classic-heroes"
        },
        {
          id: "sonic-knuckles-3",
          name: "Sonic & Knuckles + Sonic 3",
          thumbnail: "/images/games/29209_0e3475eb006699a6378ccb3f4440ab9489f0df0a.webp",
          description: "The complete Sonic 3 experience with Sonic & Knuckles lock-on technology.",
          color: "blue",
          url: "/games/29209-sonic-knuckles-3"
        },
        {
          id: "sonic-hedgehog-2",
          name: "Sonic the Hedgehog 2",
          thumbnail: "/images/games/17236_b4a87780cb8e26771c3a4f20505e80605fd272ea.webp",
          description: "The classic sequel that introduced Tails and the spin dash move.",
          color: "blue",
          url: "/games/17236-sonic-hedgehog-2"
        },
        {
          id: "sonic-2-heroes",
          name: "Sonic 2 Heroes",
          thumbnail: "/images/games/42282_15c8060c46bba364cc04720a89aab1b247e01bac.webp",
          description: "A hack of Sonic 2 that allows playing with multiple characters simultaneously.",
          color: "blue",
          url: "/games/42282-sonic-2-heroes"
        },
        {
          id: "sonic-3",
          name: "Sonic the Hedgehog 3",
          thumbnail: "/images/games/30002_eb29ae6d432f1c7055dd56a784481df11f2514a5.webp",
          description: "The third major Sonic game with expanded zones and save features.",
          color: "blue",
          url: "/games/30002-sonic-3"
        },
        {
          id: "sonic-generations-2",
          name: "Sonic Generations 2",
          thumbnail: "/images/games/42251_3eef350d7662446f9cd290f14802e15f0351844b.webp",
          description: "A fan-made sequel to Sonic Generations featuring classic and modern gameplay.",
          color: "blue",
          url: "/games/42251-sonic-generations-2"
        }
      ]
    },
    {
      title: "Fighting Games",
      color: "red",
      games: [
        {
          id: "tekken-3",
          name: "Tekken 3",
          thumbnail: "/images/games/40238_0ae25ae7f0f2fdd2ff1546d5c92cc5dabe06095c.webp",
          description: "One of the best fighting games ever made, featuring 3D gameplay and a roster of memorable characters.",
          color: "red",
          url: "/games/40238-tekken-3"
        },
        {
          id: "marvel-vs-capcom",
          name: "Marvel vs Capcom",
          thumbnail: "/images/games/mvscu.webp",
          description: "The legendary crossover fighting game featuring Marvel superheroes and Capcom characters.",
          color: "purple",
          url: "/games/mvscu"
        },
        {
          id: "jojos-bizarre-adventure",
          name: "JoJo's Bizarre Adventure",
          thumbnail: "/images/games/8843_660ca881b33a86ab9b3c21b5de5fbdcabaeca0cc.webp",
          description: "A fighting game based on the popular JoJo's Bizarre Adventure manga/anime series.",
          color: "yellow",
          url: "/games/8843-jojos-bizarre-adventure"
        },
        {
          id: "street-fighter-2",
          name: "Street Fighter II: Champion Edition",
          thumbnail: "/images/games/sf2ce.webp",
          description: "The iconic fighting game that defined the genre with its competitive gameplay.",
          color: "green",
          url: "/games/sf2ce"
        },
        {
          id: "king-of-fighters-2002",
          name: "The King of Fighters 2002 Super",
          thumbnail: "/images/games/kf2k2plc.webp",
          description: "A popular entry in SNK's long-running fighting game series with a large roster of characters.",
          color: "orange",
          url: "/games/kf2k2plc"
        },
        {
          id: "dbz-buyuu-retsuden",
          name: "Dragon Ball Z - Buyuu Retsuden",
          thumbnail: "/images/games/16913_52bd91aed0fa464e3b14ac8a3a47723b6937482b.webp",
          description: "A fighting game based on the Dragon Ball Z anime series for the Sega Genesis.",
          color: "orange",
          url: "/games/16913-dbz-buyuu-retsuden"
        }
      ]
    },
    {
      title: "Racing Games",
      color: "green",
      games: [
        {
          id: "crash-team-racing",
          name: "Crash Team Racing",
          thumbnail: "/images/games/41687_b197ecc70b99140ce92d9d9c16a6e5f0f0520bad.webp",
          description: "A kart racing game featuring characters from the Crash Bandicoot series.",
          color: "orange",
          url: "/games/41687-crash-team-racing"
        },
        {
          id: "top-gear",
          name: "Top Gear",
          thumbnail: "/images/games/23881_2ee092593fc532584b30e9f49a69eec38f301c86.webp",
          description: "A classic racing game known for its split-screen gameplay and catchy soundtrack.",
          color: "green",
          url: "/games/23881-top-gear"
        }
      ]
    },
    {
      title: "Bomberman Games",
      color: "blue",
      games: [
        {
          id: "super-bomberman-4",
          name: "Super Bomberman 4",
          thumbnail: "/images/games/19846_c1c2ac37e815f23bbd55d6357dae9d84be8b4471.webp",
          description: "The fourth entry in the Bomberman series with new modes and features.",
          color: "blue",
          url: "/games/19846-super-bomberman-4"
        },
        {
          id: "super-bomberman",
          name: "Super Bomberman",
          thumbnail: "/images/games/24792_5004814340407f47c3ff533f5691922412b09a1a.webp",
          description: "The first Bomberman game for the SNES, featuring explosive action and multiplayer fun.",
          color: "blue",
          url: "/games/24792-super-bomberman"
        },
        {
          id: "super-bomberman-5",
          name: "Super Bomberman 5",
          thumbnail: "/images/games/19907_0cca214ede7c19c65cd0cc21448ab1a8caab4275.webp",
          description: "The final Bomberman game for the SNES with enhanced battle mode and new features.",
          color: "yellow",
          url: "/games/19907-super-bomberman-5"
        },
        {
          id: "neo-bomberman",
          name: "Neo Bomberman",
          thumbnail: "/images/games/neobombe.webp",
          description: "The Neo Geo version of the classic Bomberman series with enhanced graphics.",
          color: "red",
          url: "/games/neobombe"
        }
      ]
    },
    {
      title: "Platform Games",
      color: "purple",
      games: [
        {
          id: "crash-bandicoot",
          name: "Crash Bandicoot",
          thumbnail: "/images/games/40784_585d4c9e9577ae9a3a251104b9f475d93d8c4dc8.webp",
          description: "The original 3D platformer that introduced the iconic marsupial mascot.",
          color: "orange",
          url: "/games/40784-crash-bandicoot"
        },
        {
          id: "sonic-master-system",
          name: "Sonic The Hedgehog (Master System)",
          thumbnail: "/images/games/30899_acc786ae8f4fe4ef9ff4a74d5c3a77fabaa240ca.webp",
          description: "The 8-bit version of Sonic created specifically for the SEGA Master System.",
          color: "blue",
          url: "/games/30899-sonic-master-system"
        }
      ]
    },
    {
      title: "Multiplayer Games",
      color: "yellow",
      games: [
        {
          id: "super-smash-bros",
          name: "Super Smash Bros.",
          thumbnail: "/images/games/32117_c83d24e3e7994fb4ba6d4c59cc39928df1cb6e0d.webp",
          description: "The original crossover fighting game featuring Nintendo's most popular characters.",
          color: "red",
          url: "/games/32117-super-smash-bros"
        },
        {
          id: "dbz-supersonic-warriors",
          name: "Dragon Ball Z - Supersonic Warriors",
          thumbnail: "/images/games/28096_d70b68d8b7a577c6d795db8277ce1202caa31fde.webp",
          description: "A fighting game featuring Dragon Ball Z characters in intense 1-on-1 battles.",
          color: "orange",
          url: "/games/28096-dbz-supersonic-warriors"
        }
      ]
    }
  ];

  // Convert between array and position-based dictionary formats for games
  const arrayToPinnedDict = (games: Array<Game>) => {
    return games.reduce((dict, game, index) => {
      dict[`${index + 1}`] = game;
      return dict;
    }, {} as Record<string, Game>);
  };

  const pinnedDictToArray = (dict: Record<string, Game>) => {
    return Object.entries(dict)
      .sort(([posA], [posB]) => parseInt(posA) - parseInt(posB))
      .map(([_pos, game]) => game);
  };

  // Load pinned games from Supabase or localStorage
  useEffect(() => {
    const loadPinnedGames = async () => {
      setIsLoadingPinnedGames(true);
      try {
        if (user) {
          // Check if user is subscribed before making API request
          if (!isSubscriber) {
            console.log('User is not subscribed, using localStorage only for games');
            const saved = localStorage.getItem('pinnedGames');
            setPinnedGames(saved ? JSON.parse(saved) : []);
            setIsLoadingPinnedGames(false);
            return;
          }
          
          // User is subscribed, proceed with Supabase request
          console.log('Attempting to load pinned games from Supabase');
          const { data, error } = await supabase
            .from('user_pinned_apps')
            .select('pinned_apps')
            .eq('user_id', user.id)
            .single();
          
          if (error) {
            if (error.code !== 'PGRST116') { // Not found error
              console.error('Error loading pinned games from Supabase:', error);
            }
            
            // If not found or error, fall back to localStorage
            const localGames = localStorage.getItem('pinnedGames');
            if (localGames) {
              const parsedGames = JSON.parse(localGames);
              setPinnedGames(parsedGames);
              
              // Save local games to Supabase for future use using the new format
              await supabase.from('user_pinned_apps').upsert({
                user_id: user.id,
                pinned_apps: {
                  theatre: {}, // Empty theatre object if this is a new record
                  games: arrayToPinnedDict(parsedGames)
                }
              });
              console.log('Saved local pinned games to Supabase with new format');
            } else {
              setPinnedGames([]);
            }
          } else if (data) {
            console.log('Found pinned apps/games in Supabase:', data.pinned_apps);
            // Get the games section from the nested structure
            const gamesData = data.pinned_apps.games || {};
            
            // Convert from position dictionary to array
            const gamesArray = pinnedDictToArray(gamesData);
            setPinnedGames(gamesArray);
            
            // Update localStorage for offline access
            localStorage.setItem('pinnedGames', JSON.stringify(gamesArray));
          }
        } else {
          // Not logged in, use localStorage only
          console.log('User not authenticated, using localStorage only for games');
          const saved = localStorage.getItem('pinnedGames');
          setPinnedGames(saved ? JSON.parse(saved) : []);
        }
      } catch (error) {
        console.error('Error loading pinned games:', error);
        // Fall back to localStorage on any error
        const saved = localStorage.getItem('pinnedGames');
        setPinnedGames(saved ? JSON.parse(saved) : []);
      } finally {
        setIsLoadingPinnedGames(false);
      }
    };
    
    loadPinnedGames();
  }, [user, isSubscriber]);

  // Save pinned games to Supabase and localStorage whenever they change
  useEffect(() => {
    const savePinnedGames = async () => {
      // Skip saving during initial load
      if (isLoadingPinnedGames) return;
      
      // Always save to localStorage for offline access
      localStorage.setItem('pinnedGames', JSON.stringify(pinnedGames));
      
      // If user is logged in and subscribed, save to Supabase
      if (user && isSubscriber) {
        try {
          // First, get the current record to preserve theatre data
          const { data, error: fetchError } = await supabase
            .from('user_pinned_apps')
            .select('pinned_apps')
            .eq('user_id', user.id)
            .single();
          
          // Default empty theatre object if no existing record
          const theatreData = data?.pinned_apps?.theatre || {};
          
          // Update with new games data but keep theatre data
          const { error } = await supabase.from('user_pinned_apps').upsert({
            user_id: user.id,
            pinned_apps: {
              theatre: theatreData,
              games: arrayToPinnedDict(pinnedGames)
            },
            updated_at: new Date().toISOString()
          });
          
          if (error) {
            console.error('Error saving pinned games to Supabase:', error);
          } else {
            console.log('Saved pinned games to Supabase');
          }
        } catch (error) {
          console.error('Failed to save pinned games to Supabase:', error);
        }
      } else {
        console.log('User not authenticated or not subscribed, saving games to localStorage only');
      }
    };
    
    savePinnedGames();
  }, [pinnedGames, user, isLoadingPinnedGames, isSubscriber]);

  // Toggle pin mode with subscription check
  const togglePinMode = useCallback(() => {
    if (isSubscriber) {
      setIsPinMode(current => !current);
    } else {
      // Open subscription required modal
      onOpen();
    }
  }, [isSubscriber, onOpen]);

  // Update the handlePinToggle function to clearly separate toggle mode from pinning
  const handlePinToggle = useCallback((game: Game) => {
    // Only handle actual game pinning here, not mode toggling
    if (!game.name) {
      return; // Don't handle empty games here anymore
    }
    
    setPinnedGames(current => {
      const isPinned = current.some(pinned => pinned.id === game.id);
      if (isPinned) {
        return current.filter(pinned => pinned.id !== game.id);
      } else {
        return [...current, game];
      }
    });
  }, []);

  // Handle clicking outside to exit pin mode
  const handleClickOutside = useCallback((e: MouseEvent) => {
    const target = e.target as HTMLElement;
    const isGameButton = target.closest('.game-button');
    
    if (!isGameButton && isPinMode) {
      setIsPinMode(false);
    }
  }, [isPinMode]);

  useEffect(() => {
    if (isPinMode) {
      document.addEventListener('click', handleClickOutside);
    } else {
      document.removeEventListener('click', handleClickOutside);
    }
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isPinMode, handleClickOutside]);

  // Get all games as a flat array
  const allGames = gameCategories.flatMap(category => 
    category.games.map(game => ({
      ...game,
      isPinned: pinnedGames.some(pinned => pinned.id === game.id)
    }))
  );

  // Add isPinned flag to all games
  const categoriesWithPins = gameCategories.map(category => ({
    ...category,
    games: category.games.map(game => ({
      ...game,
      isPinned: pinnedGames.some(pinned => pinned.id === game.id)
    }))
  }));

  return (
    <Box 
      bg="#000" 
      color={textColor} 
      minHeight="100vh" 
      height="100vh"
      overflow="hidden"
      bgGradient={bgGradient}
      position="relative"
      _before={{
        content: '""',
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundImage: "url('https://images.unsplash.com/photo-1511512578047-dfb367046420?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2071&q=80')",
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        opacity: 0.08,
        zIndex: 0
      }}
    >
      {/* Header */}
      <AppHeader
        playbackState={null}
        layoutMode="split"
        setLayoutMode={() => {}}
        accentColor="#4A5568"
        pageTitle="Games"
        isCondensed={false}
      />

      {/* Main Content */}
      <Box 
        as="main" 
        position="absolute"
        top="80px"
        bottom={0}
        left={0}
        right={0}
        overflowY="auto"
        zIndex={1}
      >
        <Container maxW="1200px" py={8} px={4}>
          {/* Fullscreen Toggle */}
          <Flex 
            justify="center" 
            mb={8}
            position="relative"
            _before={{
              content: '""',
              position: 'absolute',
              top: '50%',
              left: 0,
              right: 0,
              height: '1px',
              bg: 'rgba(255, 255, 255, 0.1)',
              zIndex: 0
            }}
          >
            {/* Top pin button removed */}
          </Flex>

          {/* Pinned Games Section */}
          <VStack spacing={4} mb={8} align="stretch">
            <Heading fontSize="2xl" textAlign="center" color="white">
              Your Pinned Games
            </Heading>
            
            {isLoadingPinnedGames ? (
              <Box 
                py={4} 
                textAlign="center" 
                width="100%" 
                borderRadius="lg"
                borderWidth="1px"
                borderColor="whiteAlpha.100"
                bg="rgba(0, 0, 0, 0.3)"
              >
                <Box 
                  mx="auto" 
                  width="40px" 
                  height="40px" 
                  borderRadius="50%" 
                  border="3px solid rgba(255,255,255,0.1)" 
                  borderTopColor="blue.400"
                  animation={`${pulseGlow} 3s infinite`}
                />
                <Text mt={2} color="gray.400" fontSize="sm">Loading your games...</Text>
              </Box>
            ) : pinnedGames.length === 0 ? (
              <Box 
                py={8} 
                textAlign="center" 
                width="100%" 
                borderRadius="lg"
                borderWidth="1px"
                borderColor="whiteAlpha.100"
                bg="rgba(0, 0, 0, 0.3)"
              >
                <HStack spacing={4} px={4} py={2} wrap="wrap" justify="center">
                  <AddPinButton 
                    onClick={togglePinMode} 
                    isPinMode={isPinMode}
                    type="game"
                    color="purple"
                  />
                </HStack>
                <Text color="gray.400" mt={4}>
                  No games pinned yet. Toggle "Pin Games" mode and click on any game below to add it here.
                </Text>
              </Box>
            ) : (
              <Box 
                position="relative"
                overflowX="auto"
                p={1}
                css={{
                  '&::-webkit-scrollbar': {
                    display: 'none'
                  },
                  msOverflowStyle: 'none',
                  scrollbarWidth: 'none'
                }}
              >
                <HStack spacing={4} px={4} py={2} wrap="wrap" justify="center">
                  <AddPinButton 
                    onClick={togglePinMode} 
                    isPinMode={isPinMode}
                    type="game"
                    color="purple"
                  />
                  {pinnedGames.map((game) => (
                    <Box key={game.id} flex="0 0 auto">
                      <AppButton 
                        item={{
                          id: game.id,
                          name: game.name,
                          url: game.url || `/games/${game.id}`,
                          color: game.color,
                          isPinned: true,
                          isPinMode: isPinMode,
                          description: game.description
                        }}
                        type="game"
                        imageUrl={game.thumbnail}
                        onClick={() => navigate(game.url || `/games/${game.id}`)}
                        onPinToggle={() => handlePinToggle(game)}
                      />
                    </Box>
                  ))}
                </HStack>
              </Box>
            )}
          </VStack>

          {/* Game Categories */}
          {categoriesWithPins.map(category => (
            <CategoryRow 
              key={category.title}
              title={category.title}
              games={category.games}
              color={category.color}
              onPinToggle={handlePinToggle}
              isPinMode={isPinMode}
            />
          ))}
        </Container>
      </Box>

      {/* Subscription Required Modal */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay backdropFilter="blur(10px)" />
        <ModalContent bg="gray.900" color="white" borderRadius="xl">
          <ModalHeader fontSize="xl">Pro Feature</ModalHeader>
          <ModalBody>
            <Text mb={4}>
              Pin Mode is only available for Pro subscribers. Upgrade to Pro to pin your favorite games for quick access!
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" mr={3} onClick={onClose}>
              Maybe Later
            </Button>
            <Button colorScheme="blue" onClick={() => {
              onClose();
              navigate('/pricing');
            }}>
              View Pricing
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default GamesPage;